import type { CSSProperties } from 'react';
import { StarIcon } from '@/assets/icons/icons';
import styles from './CopilotButton.module.css';
import { LBCanvasMode } from './LBCanvasMode';
import {
  LogiblocksGate,
  useLogiblocksGateEnabled,
} from './useLogiblocksGateEnabled';
import {
  COPILOT_BUTTON_LEFT,
  COPILOT_BUTTON_TOP,
  COPILOT_BUTTON_Z_INDEX,
} from './styles';

const POSITION_STYLE: CSSProperties = {
  position: 'absolute',
  top: COPILOT_BUTTON_TOP,
  left: COPILOT_BUTTON_LEFT,
  zIndex: COPILOT_BUTTON_Z_INDEX,
};

interface CopilotButtonProps {
  isDraft: boolean;
  canvasMode: LBCanvasMode;
  /** Whether the chat panel is currently open -- hides the button so it
   * doesn't sit as a "ghost" element underneath the left-anchored panel. */
  isOpen: boolean;
  /** True while the workflow hasn't been persisted yet (no id) -- shown
   * greyed out rather than hidden, so it's clear Copilot will be available
   * once the workflow exists. */
  disabled?: boolean;
  /** Existing durable draft recovery must stay reachable even when the
   * rollout gate is disabled; this never authorizes a new model turn. */
  recoveryAvailable?: boolean;
  onClick: () => void;
}

export function CopilotButton({
  isDraft,
  canvasMode,
  isOpen,
  disabled = false,
  recoveryAvailable = false,
  onClick,
}: CopilotButtonProps) {
  const isAiBuilderEnabled = useLogiblocksGateEnabled(
    LogiblocksGate.AI_BUILDER
  );

  if (
    (!isAiBuilderEnabled && !recoveryAvailable) ||
    !isDraft ||
    canvasMode !== LBCanvasMode.EDITING ||
    isOpen
  ) {
    return null;
  }

  return (
    <button
      type='button'
      onClick={onClick}
      className={styles.pill}
      // eslint-disable-next-line react/forbid-dom-props -- The position comes from the COPILOT_BUTTON_* layout constants in styles.ts.
      style={POSITION_STYLE}
      title='Co-pilot'
      aria-label='Open Copilot'
      aria-pressed={isOpen}
      disabled={disabled}
    >
      <StarIcon color='currentColor' />
      <span>Co-pilot</span>
    </button>
  );
}
